
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { Validate, Provider } from '@/types';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';
import Toast from '@/utils/toast';
import DatePicker from '@/components/shared/DatePicker.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'auth';

type MutableCredit = {
	name: string;
	email: string;
	expiry: string | null;
	balance: number | null;
}

@Component<CreditModal>({
	components: {
		TextField,
		StandardModal,
		TextButton,
		InfoBanner,
		DatePicker,
		WarningModal,
		ValidationProvider,
		ValidationObserver
	}
})
export default class CreditModal extends Vue {
	@Action('expireCredit', { namespace }) private expireCredit!: (email: string) => Promise<void>;
	@Action('saveCredit', { namespace }) private saveCredit!: (payload: { id: string|undefined; credit: App8Credit }) => Promise<void>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;
	@Prop({ type: Object, required: false, default: null }) private credit!: App8Credit | null;
	@Prop({ type: Array, required: false, default: () => [] }) private activeCredits!: App8Credit[];

	$refs!: {
		observer: Validate;
		expiryDatePicker: DatePicker;
		provider: Provider;
	};

	private isSaving: boolean = false;
	private isExpiring: boolean = false;
	private noExpiryDate: boolean = false;
	private showExpireWarningModal: boolean = false;
	private showExistingCreditWarningModal: boolean = false;
	private userNotExistError: boolean = false;
	private foundCredit: App8Credit | null = null;
	private mutableCredit: MutableCredit = {
		name: '',
		email: '',
		expiry: '',
		balance: null
	};

	private get isExpired(): boolean {
		return !!this.credit && this.credit.expiry && this.credit.expiry <= DateTime.now();
	}

	/**
	 * The minimum date that can be selected in the datepicker based on the restaurant's timezone
	 *
	 * @return {string}
	 */
	private get minDate(): string {
		return DateTime.now().setZone(this.restaurant.menu_configuration?.zone || 'America/Toronto').toISODate();
	}

	private created(): void {
		if (this.credit) {
			const { id, ...credit } = this.credit;
			this.mutableCredit = { ...cloneDeep(credit), expiry: credit.expiry?.toFormat('yyyy-MM-dd') || '' };
			this.noExpiryDate = !this.mutableCredit.expiry;
		}
		else if (this.restaurant.menu_manager_config?.end_school_year_date) {
			this.mutableCredit.expiry = this.restaurant.menu_manager_config.end_school_year_date;
		}
	}

	/**
	 * Empty the expiry date picker if the checkbox is checked.
	 *
	 * @return {void}
	 */
	private emptyExpiryInput(): void {
		if (this.noExpiryDate && this.mutableCredit.expiry?.length) {
			this.$refs.expiryDatePicker.handleClear();
		}
	}

	/**
	 * Build the payload when saving a credit.
	 *
	 * @return {{ id: string|undefined; credit: App8Credit }}
	 */
	private buildSaveCreditPayload(): { id: string|undefined; credit: App8Credit } {
		// In the case of assigning a new credit to a user with already an active credit, we add up the balances
		const originalBalance = this.foundCredit?.balance || 0;
		const newBalance = (this.mutableCredit.balance as number) + originalBalance;

		const creditId: string | undefined = this.credit?.id || this.foundCredit?.id;

		const credit = {
			name: this.mutableCredit.name,
			email: this.mutableCredit.email,
			balance: newBalance,
			...(!this.noExpiryDate && { expiry: this.mutableCredit.expiry })
		};

		return { id: creditId , credit };
	}

	/**
	 * Save the credit.
	 *
	 * @return {Promise<void>}
	 */
	private async save(): Promise<void> {
		this.userNotExistError = false;

		const isValid: boolean = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}

		// Check if the email is already used in another credit if assigning a new credit
		if (!this.credit && !this.showExistingCreditWarningModal && this.doesEmailAlreadyHaveCredit()) {
			this.showExistingCreditWarningModal = true;
			return;
		}

		this.showExistingCreditWarningModal = false;
		this.isSaving = true;

		try {
			const payload = this.buildSaveCreditPayload();
			await this.saveCredit(payload);

			this.isSaving = false;
			this.$emit('re-fetch');
			this.closeModal();
		}
		catch (errorMessage) {
			// Specific handling for no user found for that email
			if (errorMessage === 'user_not_found') {
				this.userNotExistError = true;
				this.$refs.provider.setErrors([this.$t('settings.credits.modal.user.user_not_exist')]);
			}
			// Generic error handling
			else {
				Toast.error(this.$t('settings.credits.error_assigning', { errorMessage }));
			}
			this.isSaving = false;
		}
	}

	/**
	 * Check if the email already exists in the active credits list and save that credit if found.
	 *
	 * @return {boolean}
	 */
	private doesEmailAlreadyHaveCredit(): boolean {
		const foundCredit = this.activeCredits.find(credit => credit.email.toLowerCase() === this.mutableCredit.email.toLowerCase());
		if (foundCredit) {
			this.foundCredit = foundCredit;
			return true;
		}
		else {
			this.foundCredit = null;
			return false;
		}
	}

	/**
	 * Expire the credit.
	 *
	 * @return {Promise<void>}
	 */
	private async expire(): Promise<void> {
		this.isExpiring = true;
		try {
			await this.expireCredit(this.credit!.email);
			this.isExpiring = false;
			this.showExpireWarningModal = false;
			this.$emit('re-fetch');
			this.closeModal();
		}
		catch (errorMessage) {
			this.isExpiring = false;
			Toast.error(this.$t('settings.credits.error_expiring', { errorMessage }));

		}
	}

	/**
	 * Emit event to close modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}
}
